import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { KeyboardService, PlatformService } from '@ld.consumer-portal/utils';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';

export interface BorrowerIdentityData {
  id: string;
  firstName: string;
  lastName: string;
  isPrimary: boolean;
  verifyRequired: boolean;
}

export interface BorrowerIdentityResult {
  borrower: BorrowerIdentityData;
  ssnLastFour?: string;
}

export interface BorrowerIdentityDialogData {
  borrowers: BorrowerIdentityData[];
  preselectedBorrower: BorrowerIdentityData | null;
  primaryOnly: boolean;
  profileSelectionMode?: boolean;
  onVerify: (result: BorrowerIdentityResult) => Promise<BorrowerIdentityDialogVerificationResult>;
}

export type BorrowerIdentityDialogVerificationResult = { ok: true } | { ok: false; error: string };

export type BorrowerIdentityDialogResult = BorrowerIdentityResult | BorrowerIdentityCancelResult;

export interface BorrowerIdentityCancelResult {
  action: 'cancel';
}

@Component({
  selector: 'ld-borrower-identity-modal',
  imports: [BaseModalComponent, MatIconModule, MatProgressSpinnerModule, NgTemplateOutlet, TranslocoDirective],
  templateUrl: './borrower-identity-modal.component.html',
  styleUrl: './borrower-identity-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BorrowerIdentityModalComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly platformService = inject(PlatformService);
  private readonly keyboardService = inject(KeyboardService);
  private readonly dialogRef = inject<DialogRef<BorrowerIdentityDialogResult>>(DialogRef, { optional: true });
  private readonly dialogData = inject<BorrowerIdentityDialogData | null>(DIALOG_DATA, { optional: true });

  /**
   * On the native mobile app, this modal renders as a bottom sheet that is
   * aware of the on-screen keyboard height so the Verify button never gets
   * hidden behind it (see CP-1069). On web, the standard centered base modal
   * is used since there's no on-screen keyboard overlay to account for.
   */
  protected readonly isBottomSheet = this.platformService.isNativeMobile;

  protected readonly keyboardHeight = this.keyboardService.keyboardOverlayHeight;

  readonly borrowers = input<BorrowerIdentityData[]>([]);

  readonly preselectedBorrower = input<BorrowerIdentityData | null>(null);

  /**
   * When true, only the primary borrower may complete verification. Non-primary
   * borrower cards remain selectable so the user gets feedback, but the Verify
   * button is disabled and an inline error explains why. Opt-in so existing
   * consumers (e.g. borrower-switch context) keep their multi-borrower flow.
   */
  readonly primaryOnly = input(false);

  readonly profileSelectionMode = input(false, { transform: booleanAttribute });

  readonly verify = output<BorrowerIdentityResult>();

  readonly cancel = output<void>();

  protected readonly isSwitchMode = computed(() => this.effectivePreselectedBorrower() !== null);

  protected readonly modalTitle = computed(() =>
    this.isProfileSelectionStep()
      ? this.transloco.translate('profileMenuSwitchProfile')
      : this.isSwitchMode()
        ? this.transloco.translate('commonMessagesVerifyIdentity')
        : this.transloco.translate('verifyIdentityModalTitle'),
  );

  protected readonly selectedBorrowerId = signal<string | null>(null);

  protected readonly ssnValues = signal<Record<string, string>>({});

  protected readonly isLoading = signal(false);

  protected readonly errorMessage = signal<string | null>(null);

  private readonly ssnInputRef = viewChild<ElementRef<HTMLInputElement>>('ssnInput');
  private readonly profileOptionRefs = viewChildren<ElementRef<HTMLButtonElement>>('borrowerProfileOption');

  protected readonly effectiveBorrowers = computed(() => this.dialogData?.borrowers ?? this.borrowers());

  protected readonly effectivePreselectedBorrower = computed(
    () => this.dialogData?.preselectedBorrower ?? this.preselectedBorrower(),
  );

  protected readonly effectivePrimaryOnly = computed(() => this.dialogData?.primaryOnly ?? this.primaryOnly());

  protected readonly effectiveProfileSelectionMode = computed(
    () => this.dialogData?.profileSelectionMode ?? this.profileSelectionMode(),
  );

  protected readonly isProfileSelectionStep = signal(false);

  private readonly shouldFocusProfileOption = signal(false);

  constructor() {
    if (this.platformService.isNativeMobile() && this.dialogRef) {
      this.keyboardService.stopSuppression();
      this.dialogRef.overlayRef.updateSize({ width: '100vw', maxWidth: '100vw' });
    }

    effect(() => {
      this.isProfileSelectionStep.set(this.effectiveProfileSelectionMode());
    });

    effect(() => {
      const preselected = this.effectivePreselectedBorrower();
      if (preselected) {
        this.selectedBorrowerId.set(preselected.id);
      }
    });

    effect(() => {
      const id = this.selectedBorrowerId();
      if (id && !this.isProfileSelectionStep()) {
        setTimeout(() => this.ssnInputRef()?.nativeElement.focus());
      }
    });

    effect(() => {
      if (!this.isProfileSelectionStep() || !this.shouldFocusProfileOption()) return;

      const options = this.profileOptionRefs();
      if (!options.length) return;

      const selectedId = this.selectedBorrowerId();
      const selectedIndex = options.findIndex(
        (option) => option.nativeElement.id === `borrower-profile-option-${selectedId}`,
      );
      const option = options[selectedIndex >= 0 ? selectedIndex : 0];

      setTimeout(() => {
        option.nativeElement.focus();
        this.shouldFocusProfileOption.set(false);
      });
    });

    // Surface a primary-only violation as soon as a non-primary borrower is
    // selected, and clear it when the user selects the primary borrower.
    effect(() => {
      if (!this.effectivePrimaryOnly()) return;
      const selected = this.selectedBorrower();
      if (selected && !selected.isPrimary) {
        this.errorMessage.set('modalsBorrowerIdentityPrimaryOnlyError');
      } else if (selected?.isPrimary && this.errorMessage() === 'modalsBorrowerIdentityPrimaryOnlyError') {
        this.errorMessage.set(null);
      }
    });
  }

  protected readonly selectedBorrower = computed(() => {
    const id = this.selectedBorrowerId();
    if (!id) return null;
    return this.effectiveBorrowers().find((b) => b.id === id) ?? null;
  });

  protected readonly displayBorrower = computed(() =>
    this.effectiveProfileSelectionMode() ? this.selectedBorrower() : this.effectivePreselectedBorrower(),
  );

  protected readonly isEnterDisabled = computed(() => {
    if (this.isLoading()) return true;
    const selected = this.selectedBorrower();
    if (!selected) return true;
    // Must precede the verifyRequired short-circuit so a non-primary borrower
    // with verifyRequired: false can't slip past when primaryOnly is set.
    if (this.effectivePrimaryOnly() && !selected.isPrimary) return true;
    if (!selected.verifyRequired) return false;
    const ssn = this.ssnValues()[selected.id] ?? '';
    return ssn.length !== 4;
  });

  protected onSelectBorrower(borrower: BorrowerIdentityData): void {
    this.shouldFocusProfileOption.set(false);
    if (this.selectedBorrowerId() !== borrower.id) {
      this.selectedBorrowerId.set(borrower.id);
      this.errorMessage.set(null);
    }
    this.isProfileSelectionStep.set(false);
  }

  protected onChangeProfile(): void {
    if (!this.isLoading()) {
      this.errorMessage.set(null);
      this.isProfileSelectionStep.set(true);
      this.shouldFocusProfileOption.set(true);
    }
  }

  protected isSelected(borrower: BorrowerIdentityData): boolean {
    return this.selectedBorrowerId() === borrower.id;
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !this.isEnterDisabled()) {
      event.preventDefault();
      this.onEnter();
    }
  }

  protected onSsnInput(event: Event, borrowerId: string): void {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/\D/g, '').slice(0, 4);
    input.value = sanitized;
    this.ssnValues.update((values) => ({ ...values, [borrowerId]: sanitized }));
    this.errorMessage.set(null);
  }

  protected getSsnValue(borrowerId: string): string {
    return this.ssnValues()[borrowerId] ?? '';
  }

  protected getBorrowerDisplayName(borrower: BorrowerIdentityData): string {
    return `${borrower.firstName} ${borrower.lastName}`.trim();
  }

  protected getBorrowerTypeLabel(borrower: BorrowerIdentityData): string {
    return borrower.isPrimary ? 'verifyIdentityPrimaryBorrower' : 'tooltipCoBorrowerTitle';
  }

  protected async onEnter(): Promise<void> {
    const selected = this.selectedBorrower();
    if (!selected) {
      this.errorMessage.set('modalsBorrowerIdentitySelectError');
      return;
    }

    let result: BorrowerIdentityResult;

    if (!selected.verifyRequired) {
      result = {
        borrower: selected,
        ssnLastFour: undefined,
      };
    } else {
      const ssn = this.ssnValues()[selected.id] ?? '';
      if (ssn.length !== 4) {
        this.errorMessage.set('modalsBorrowerIdentitySsnError');
        return;
      }

      result = {
        borrower: selected,
        ssnLastFour: ssn,
      };
    }

    this.isLoading.set(true);

    if (this.dialogData && this.dialogRef) {
      try {
        const verification = await this.dialogData.onVerify(result);
        if (verification.ok) {
          this.dialogRef.close(result);
        } else {
          this.setError(verification.error);
        }
      } catch {
        this.setError('Verification failed. Please try again.');
      }
      return;
    }

    this.verify.emit(result);
  }

  protected onCancel(): void {
    if (!this.isLoading()) {
      if (this.dialogData && this.dialogRef) {
        this.dialogRef.close({ action: 'cancel' });
      } else {
        this.cancel.emit();
      }
    }
  }

  setError(message: string): void {
    this.errorMessage.set(message);
    this.isLoading.set(false);
  }

  setLoading(loading: boolean): void {
    this.isLoading.set(loading);
  }

  reset(): void {
    this.selectedBorrowerId.set(null);
    this.ssnValues.set({});
    this.errorMessage.set(null);
    this.isLoading.set(false);
    this.isProfileSelectionStep.set(this.effectiveProfileSelectionMode());
  }
}
